/**
 * HeroSection Component
 * Design: "Signal & Noise" – Dark Tech
 * Split layout: Text + CTAs left, Profile Photo right
 * SCROLL indicator removed
 */
import { ArrowRight, TrendingUp, Users, Award } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLanguage } from "@/contexts/LanguageContext";

const HERO_BG = "/manus-storage/hero-bg_97a2c823.webp";
const HERO_BG_MOBILE = "/manus-storage/hero-bg-mobile_1e548665.webp";
const PROFILE_PHOTO = "/manus-storage/cem-yigit-photo-new_faed3f56.webp";
const PROFILE_PHOTO_MOBILE = "/manus-storage/cem-yigit-photo-mobile_04990804.webp";

function useCountUp(target: number, duration: number = 2000, start: boolean = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

function StatCard({ icon: Icon, value, suffix, label, color, delay, started }: {
  icon: React.ElementType;
  value: number;
  suffix: string;
  label: string;
  color: string;
  delay: number;
  started: boolean;
}) {
  const count = useCountUp(value, 2000, started);
  return (
    <div
      className="stat-card rounded-xl p-3 sm:p-4 flex items-center gap-2 sm:gap-3 min-w-0 flex-1 basis-[calc(50%-0.25rem)] sm:basis-[calc(33%-0.5rem)]"
    >
      <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${color}`}>
        <Icon size={16} className="text-white sm:hidden" />
        <Icon size={20} className="text-white hidden sm:block" />
      </div>
      <div className="min-w-0">
        <div className="font-mono-custom text-xl sm:text-2xl font-bold text-white leading-none">
          {count}{suffix}
        </div>
        <div className="text-xs text-slate-400 mt-1 font-inter leading-tight">{label}</div>
      </div>
    </div>
  );
}

export default function HeroSection() {
  const [statsStarted, setStatsStarted] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const { t } = useLanguage();

  useEffect(() => {
    const timer = setTimeout(() => setStatsStarted(true), 800);
    return () => clearTimeout(timer);
  }, []);

  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };
  const scrollToRefs = () => {
    document.querySelector("#references")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      ref={sectionRef}
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
      style={{ background: "#080C14" }}
    >
      {/* Background Image – als <picture> damit Mobile die kleine Version lädt */}
      <picture className="absolute inset-0" aria-hidden="true">
        <source media="(max-width: 767px)" srcSet={HERO_BG_MOBILE} type="image/webp" />
        <source srcSet={HERO_BG} type="image/webp" />
        <img
          src={HERO_BG}
          alt=""
          fetchPriority="low"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover object-center"
          style={{ opacity: 0.25 }}
        />
      </picture>
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#080C14] via-[#080C14]/70 to-[#080C14]/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#080C14] via-transparent to-[#080C14]/40" />

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `linear-gradient(rgba(59,130,246,1) 1px, transparent 1px), linear-gradient(90deg, rgba(59,130,246,1) 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-36 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center" id="hero-grid">

          {/* LEFT: Text content */}
          <div className="max-w-2xl">
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 bg-blue-500/10 border border-blue-500/20 rounded-full px-4 py-1.5 mb-8"
            >
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-sm text-blue-300 font-sora font-medium">{t.hero.badge}</span>
            </div>

            {/* Headline */}
            <div>
              {/* Mobile: Foto neben Headline */}
              <div className="flex items-start gap-4 lg:block">
                <div className="flex-1">
                  <h1 className="font-sora font-black text-4xl sm:text-6xl lg:text-7xl leading-[1.05] text-white mb-2">
                    {t.hero.headline1}
                  </h1>
                  <h2 className="font-sora font-black text-3xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-balance mb-6">
                    <span className="text-gradient-blue">{t.hero.headline2}</span>
                  </h2>
                </div>
                {/* Kleines Foto – nur auf Mobile sichtbar */}
                <div className="flex-shrink-0 lg:hidden mt-1">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl overflow-hidden border-2 border-blue-500/40 shadow-lg shadow-blue-500/20">
                    <img
                      src={PROFILE_PHOTO_MOBILE}
                      sizes="96px"
                      alt="Cem Fernández-Yigit – Performance Marketing Experte"
                      fetchPriority="high"
                      decoding="async"
                      width={96}
                      height={96}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Subheadline */}
            <p
              className="text-lg sm:text-xl text-slate-300 max-w-xl leading-relaxed mb-10 font-inter"
            >
              {t.hero.subtext}
            </p>

            {/* CTA Buttons */}
            <div
              className="flex flex-col sm:flex-row gap-4 mb-12"
            >
              <a
                href={t.calendlyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-sora font-semibold group"
              >
                {t.hero.cta1}
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              <button
                onClick={scrollToRefs}
                className="btn-outline-blue flex items-center justify-center gap-2 px-8 py-4 rounded-xl text-base font-sora font-semibold"
              >
                {t.hero.cta2}
              </button>
            </div>

            {/* Stats */}
            <div
              className="flex flex-wrap gap-2"
              style={{ rowGap: "0.5rem" }}
            >
              <StatCard
                icon={Award}
                value={20}
                suffix="+"
                label={t.hero.yearsLabel}
                color="bg-blue-600"
                delay={0.5}
                started={statsStarted}
              />
              <StatCard
                icon={Users}
                value={80}
                suffix="+"
                label={t.hero.clientsLabel}
                color="bg-emerald-600"
                delay={0.6}
                started={statsStarted}
              />
              <StatCard
                icon={TrendingUp}
                value={10}
                suffix="+"
                label={t.hero.platformsLabel}
                color="bg-blue-700"
                delay={0.7}
                started={statsStarted}
              />
            </div>

          </div>

          {/* RIGHT: Profile Photo */}
          <div className="hidden lg:flex justify-center items-center relative">
            {/* Glow behind photo */}

            <div className="relative w-72 xl:w-80">
              {/* Decorative rings */}
              <div className="absolute -inset-3 rounded-2xl border border-blue-500/20" />
              <div className="absolute -inset-6 rounded-3xl border border-blue-500/8" />

              {/* Photo */}
              <div className="w-full aspect-[3/4] relative rounded-2xl overflow-hidden border border-blue-500/30 shadow-2xl shadow-blue-500/20">
                <img
                  src={PROFILE_PHOTO}
                  alt="Cem Fernández-Yigit – Performance Marketing Experte"
                  fetchPriority="high"
                  decoding="async"
                  width={384}
                  height={512}
                  className="w-full h-full object-cover object-top"
                />
                {/* Name overlay at bottom */}
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#080C14] via-[#080C14]/70 to-transparent pt-20 pb-5 px-5">
                  <div className="font-sora font-black text-xl text-white">Cem Fernández-Yigit</div>
                  <div className="text-blue-400 font-sora font-medium text-sm">M.Sc. Online Marketing</div>
                  <div className="mt-1 text-xs leading-snug text-slate-300 font-inter">{t.hero.profileTeaching}</div>
                </div>
              </div>

              {/* Floating badge top-right */}
              <div className="absolute -top-4 -right-4 bg-[#0D1526] border border-blue-500/30 rounded-xl px-4 py-3 shadow-xl">
                <div className="text-blue-400 font-sora font-bold text-sm">20+ {t.hero.yearsLabel.split(" ")[0]}</div>
                <div className="text-slate-400 text-xs font-inter">{t.hero.yearsLabel.split(" ").slice(1).join(" ")}</div>
              </div>

            </div>
          </div>

        </div>

      </div>
    </section>
  );
}
